import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';
import { useEffect as useReactEffect } from 'react';

type Message = Database['public']['Tables']['messages']['Row'];

export interface ConversationPartner {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  company: string | null;
}

export interface Conversation {
  partnerId: string;
  partner: ConversationPartner | undefined;
  lastMessage: Message;
  unreadCount: number;
}

interface ConversationRow {
  partner_id: string;
  last_message_id: string;
  last_message_content: string;
  last_message_created_at: string;
  last_message_sender_id: string;
  last_message_receiver_id: string;
  last_message_is_read: boolean;
  unread_count: number;
}

export function useConversations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['conversations', user?.id],
    queryFn: async (): Promise<Conversation[]> => {
      if (!user?.id) return [];

      // Use RPC to fetch conversation summaries (avoids full message scan)
      const { data: rows, error: rpcError } = await supabase
        .rpc('get_conversations');

      if (rpcError) throw rpcError;
      if (!rows || rows.length === 0) return [];

      const partnerIds = rows.map((r: ConversationRow) => r.partner_id);

      // Fetch profiles for all partners
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, company')
        .in('user_id', partnerIds);

      if (profilesError) throw profilesError;

      // Create a map of user_id -> profile
      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) || []);

      return (rows as ConversationRow[]).map((row) => ({
        partnerId: row.partner_id,
        partner: profileMap.get(row.partner_id),
        lastMessage: {
          id: row.last_message_id,
          sender_id: row.last_message_sender_id,
          receiver_id: row.last_message_receiver_id,
          content: row.last_message_content,
          created_at: row.last_message_created_at,
          is_read: row.last_message_is_read,
        } as Message,
        unreadCount: row.unread_count ?? 0,
      }));
    },
    enabled: !!user?.id,
  });
}

export function useMessages(partnerId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const markAsRead = useMutation({
    mutationFn: async () => {
      if (!user?.id || !partnerId) return;
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('receiver_id', user.id)
        .eq('sender_id', partnerId)
        .eq('is_read', false);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unreadMessageCount'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  // Set up realtime subscription
  useReactEffect(() => {
    if (!user?.id || !partnerId) return;

    const channel = supabase
      .channel(`messages-${user.id}-${partnerId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `sender_id=eq.${partnerId}`,
        },
        (payload) => {
          const newMessage = payload.new as Message;
          queryClient.setQueryData<Message[] | undefined>(['messages', partnerId], (current) => {
            if (!current) return current;
            if (current.some((msg) => msg.id === newMessage.id)) return current;
            return [...current, newMessage];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, partnerId, queryClient]);

  // Mark incoming messages as read when opening a thread
  useReactEffect(() => {
    if (!user?.id || !partnerId) return;
    markAsRead.mutate();
  }, [user?.id, partnerId]);

  return useQuery({
    queryKey: ['messages', partnerId],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${partnerId}),and(sender_id.eq.${partnerId},receiver_id.eq.${user.id})`)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!partnerId,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ receiverId, content }: { receiverId: string; content: string }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          receiver_id: receiverId,
          content,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (newMessage, variables) => {
      queryClient.setQueryData<Message[] | undefined>(['messages', variables.receiverId], (current) => {
        if (!current) return current;
        if (current.some((msg) => msg.id === newMessage.id)) return current;
        return [...current, newMessage];
      });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['unreadMessageCount'] });
    },
  });
}

export function useUnreadMessageCount() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Set up realtime subscription for new messages
  useReactEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`unread-messages-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['unreadMessageCount'] });
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return useQuery({
    queryKey: ['unreadMessageCount', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
  });
}
